import { useEffect } from 'react';

import { ApiErrorResponse } from 'apisauce';

import { useCustomAlerts } from '@/shared/lib/alert';

import {
  IErrorResponseData,
  IErrorWithStatusCode,
  isMainError,
  TModelErrorResponseData
} from '../types/error';

type UserError = Error | IErrorWithStatusCode;

const UNKNOWN_ERROR = 'Неизвестная ошибка';

/**
 * Собирает все непустые строки из значения любой формы: строка, массив (в т.ч. вложенный),
 * объект «поле → сообщения». Бэк в части форм отдаёт `errors` не массивом.
 * @param value Значение `errors` из ответа сервера
 */
const collectErrorMessages = (value: unknown): string[] => {
  if (typeof value === 'string') return value.trim() ? [value] : [];
  if (Array.isArray(value)) return value.flatMap(collectErrorMessages);
  if (value && typeof value === 'object') {
    return Object.values(value).flatMap(collectErrorMessages);
  }

  return [];
};

/**
 * Обработка основной ошибки: всегда возвращает непустой массив строк
 * @param errorsData Данные ошибки
 */
export const _handleMainError = (errorsData: IErrorResponseData): string[] => {
  const errors = collectErrorMessages(
    (errorsData as { errors?: unknown } | null)?.errors
  );

  return errors.length > 0 ? errors : [UNKNOWN_ERROR];
};

/**
 * Обработка ошибки модели
 * @param errorsData Данные ошибки
 * @param modelKey Ключ модели
 */
export const _handleModelError = <MValue>(
  errorsData: TModelErrorResponseData<MValue>,
  modelKey: string
) => {
  const modelErrors = errorsData[modelKey];
  return modelErrors && modelErrors.errors ? modelErrors.errors : null;
};

export interface IHandleErrorsReturn<MValue> {
  mainErrors: string[];
  mainError: string;
  modelErrors: MValue | null;
}

/**
 * Обработка ошибок
 * @param root0 Ответ сервера
 * @param root0.data Данные ошибок
 * @param modelKey Ключ модели
 */
export const handleErrors = <MValue>(
  {
    data
  }: ApiErrorResponse<IErrorResponseData | TModelErrorResponseData<MValue>>,
  modelKey?: string
): IHandleErrorsReturn<MValue> => {
  const errors: IHandleErrorsReturn<MValue> = {
    mainErrors: [UNKNOWN_ERROR],
    mainError: UNKNOWN_ERROR,
    modelErrors: null
  };

  if (!data) return errors;

  if (data) {
    if (modelKey && !isMainError<MValue>(data)) {
      errors.modelErrors = _handleModelError(data, modelKey);
    } else {
      errors.mainErrors = _handleMainError(data);
    }
  }

  return { ...errors, mainError: errors.mainErrors.join(' ') };
};

/**
 * Эффект для отображения ошибки
 * @param error Ошибка
 */
export const useErrorEffect = (error: Error | null) => {
  const { showCustomAlert } = useCustomAlerts();

  useEffect(() => {
    if (error) {
      showCustomAlert(error.message, {
        severity: 'error'
      });
    }
  }, [error]);
};

/**
 * Хук, который при загрузке данных пользователя показывает синий алерт при 403
 * @param error - ошибка
 */
export const useUserErrorEffect = (error: UserError | null) => {
  const { showCustomAlert } = useCustomAlerts();

  useEffect(() => {
    if (!error) {
      return;
    }

    const isForbiddenError = 'statusCode' in error && error.statusCode === 403;
    if (isForbiddenError) {
      showCustomAlert(error.message, { severity: 'info' });
      return;
    }

    showCustomAlert(error.message, { severity: 'error' });
  }, [error, showCustomAlert]);
};

/**
 * Рекурсивно ищет первую строку ошибки в объекте modelErrors
 * @param errors - объект ошибок
 */
export const findFirstError = (errors: object): string | null => {
  /**
   * Рекурсивный поиск первой строковой ошибки внутри значения
   * @param value - текущее проверяемое значение
   */
  const findInValue = (value: unknown): string | null => {
    if (Array.isArray(value)) {
      const firstError = value.find(
        item => typeof item === 'string' && item.length > 0
      );
      return typeof firstError === 'string' ? firstError : null;
    }

    if (value && typeof value === 'object') {
      for (const nestedValue of Object.values(value)) {
        const found = findInValue(nestedValue);
        if (found) return found;
      }
    }

    return null;
  };

  return findInValue(errors);
};

/**
 * Проверяет ошибки на любом уровне вложенности в modelErrors
 * @param response - вернувшиеся данные
 */
export const imageErrorHandler = (
  response: ApiErrorResponse<IErrorResponseData>
) => {
  const { mainError, modelErrors } = handleErrors(response, 'user');

  const foundError = modelErrors ? findFirstError(modelErrors) : null;

  if (foundError) {
    throw new Error(foundError);
  }

  throw new Error(mainError);
};
